import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ArtifactEditResponse, ArtifactRef, ArtifactTarget, ProjectArtifactAPI } from './contracts';
import { selectArtifactReadPresentation } from '../../projection/canvas/artifactReadPresentation';
import { mergeArtifactTabProjection, selectArtifactReadProjection } from '../../projection/canvas/selectedArtifactProjection.js';

type Selection = ReturnType<typeof selectArtifactReadProjection>;
interface ReadState {
  status: 'idle' | 'pending' | 'ready' | 'failed'; selection: Selection | null;
  target: ArtifactTarget | null; error: Error | null; message: string; canRetry: boolean;
}
const idle = (): ReadState => ({ status: 'idle', selection: null, target: null, error: null, message: '', canRetry: false });
const targetKeys = ['project_id', 'artifact_id', 'artifact_version_id', 'artifact_type', 'content_digest'] as const;
const readFailureProtocol = new Map([
  [403, { message: '无权打开该产物', canRetry: false }],
  [404, { message: '该产物已不可用', canRetry: false }],
  [422, { message: '产物引用无效', canRetry: false }],
  [503, { message: '产物暂时无法打开', canRetry: true }],
]);
const invalidRead = (message: string) => Object.assign(new TypeError(message), { retryable: false });
const readFailure = (error: unknown) => {
  const source = Object(error) as { status?: number; retryable?: boolean };
  const normalized = error instanceof Error ? error : new Error('产物读取失败');
  const protocol = readFailureProtocol.get(source.status ?? 0);
  return { error: normalized, message: protocol?.message ?? normalized.message,
    canRetry: protocol?.canRetry ?? source.retryable ?? !source.status };
};

/** A scope-bound read owner for product selection; Catalog and Core retain all authoritative state. */
export const useSelection = ({ sessionId, projectId, projectAPI, tenantId, sessionContext }: {
  sessionId: string; projectId: string; projectAPI: Partial<ProjectArtifactAPI>; tenantId?: string;
  sessionContext?: { artifact_tab_projection?: unknown };
}) => {
  const scope = useMemo(() => ({ sessionId, projectId, projectAPI, tenantId, active: true,
    request: null as null | { key: string; controller: AbortController; promise: Promise<Selection | null> } }),
  [sessionId, projectId, projectAPI, tenantId]);
  const current = useRef(scope);
  current.current = scope;
  const [state, setState] = useState<{ scope: typeof scope; value: ReadState }>({ scope, value: idle() });
  const value = useMemo(() => state.scope === scope ? state.value : idle(), [state, scope]);
  const canonical = useRef(sessionContext);
  canonical.current = sessionContext;
  // Reconcile newer canonical slots here; presentation components never repeat this composition.
  const prepared = useMemo<ReadState>(() => {
    if (!value.selection) return value;
    try {
      return { ...value, selection: { ...value.selection, context: { ...value.selection.context,
        artifact_tab_projection: mergeArtifactTabProjection(sessionContext?.artifact_tab_projection,
          value.selection.context.artifact_tab_projection) } } };
    } catch (error) { return { ...value, status: 'failed', ...readFailure(error) }; }
  }, [value, sessionContext]);
  const accepted = useRef({ scope, value });
  const publish = useCallback((value: ReadState) => {
    // Fence save acknowledgments synchronously, before React renders a batched selection change.
    accepted.current = { scope, value };
    setState({ scope, value });
  }, [scope]);
  useEffect(() => {
    scope.active = true;
    return () => { scope.active = false; scope.request?.controller.abort(); scope.request = null; };
  }, [scope]);

  const select = useCallback((input: ArtifactTarget): Promise<Selection | null> => {
    if (!scope.active || current.current !== scope) return Promise.resolve(null);
    const target = Object.fromEntries(targetKeys.map((key) => [key, input?.[key]])) as unknown as ArtifactTarget;
    const key = JSON.stringify(targetKeys.map((field) => target[field]));
    if (scope.request?.key === key) return scope.request.promise;
    scope.request?.controller.abort();
    const controller = new AbortController();
    const request = { key, controller, promise: null as unknown as Promise<Selection | null> };
    scope.request = request;
    const isCurrent = () => scope.active && current.current === scope && scope.request === request && !controller.signal.aborted;
    publish({ ...idle(), selection: accepted.current.scope === scope ? accepted.current.value.selection : null,
      status: 'pending', target, message: '正在打开产物' });
    request.promise = Promise.resolve().then(async () => {
      if (!targetKeys.every((field) => typeof target[field] === 'string' && target[field].trim())
        || !sessionId || target.project_id !== projectId || !projectAPI.getProjectArtifact) {
        throw invalidRead('产物缺少完整版本身份或不在当前项目');
      }
      const read = await projectAPI.getProjectArtifact(projectId, target, { signal: controller.signal, session_id: sessionId });
      if (!isCurrent()) return null;
      const selection = selectArtifactReadProjection(read, target, projectId);
      if (tenantId && selection.artifactRef.scope.tenant_id !== tenantId) throw invalidRead('产物不在当前租户');
      mergeArtifactTabProjection(canonical.current?.artifact_tab_projection, selection.context.artifact_tab_projection);
      publish({ ...idle(), status: 'ready', selection, target });
      return selection;
    }).catch((error: unknown) => {
      if (isCurrent()) publish({ ...accepted.current.value, status: 'failed', ...readFailure(error) });
      return null;
    }).finally(() => { if (scope.request === request) scope.request = null; });
    return request.promise;
  }, [scope, sessionId, projectId, projectAPI, tenantId, publish]);

  const acceptSaved = useCallback((read: ArtifactEditResponse, expectedBase: ArtifactRef) => {
    if (!scope.active || current.current !== scope) return;
    // Validate before committing state; React state updaters must have no IO or cancellation effects.
    const previous = accepted.current;
    if (previous.scope !== scope || previous.value.status !== 'ready' || !previous.value.selection
      || previous.value.selection.artifactRef.artifact_id !== read.artifact_ref.artifact_id) return;
    const base: ArtifactRef = previous.value.selection.artifactRef;
    if (['artifact_id', 'artifact_type', 'artifact_version_id', 'content_digest'].some((key) =>
      base[key as keyof ArtifactRef] !== expectedBase[key as keyof ArtifactRef])
      || ['tenant_id', 'project_id'].some((key) => base.scope[key as keyof typeof base.scope]
        !== expectedBase.scope[key as keyof typeof base.scope])) return;
    if (base.artifact_type !== read.artifact_ref.artifact_type || ['tenant_id', 'project_id']
      .some((key) => base.scope[key as keyof typeof base.scope] !== read.artifact_ref.scope[key as keyof typeof base.scope])) {
      throw new TypeError('保存确认不属于所选产物');
    }
    const target = { ...read.artifact_ref, project_id: read.artifact_ref.scope.project_id };
    const selection = selectArtifactReadProjection(read, target, projectId);
    if (tenantId && selection.artifactRef.scope.tenant_id !== tenantId) throw invalidRead('产物不在当前租户');
    mergeArtifactTabProjection(canonical.current?.artifact_tab_projection, selection.context.artifact_tab_projection);
    scope.request?.controller.abort(); scope.request = null;
    publish({ ...idle(), status: 'ready', selection, target });
  }, [scope, projectId, tenantId, publish]);
  const retry = useCallback(() => prepared.canRetry && prepared.target ? select(prepared.target) : Promise.resolve(null),
    [prepared.canRetry, prepared.target, select]);
  const clear = useCallback(() => {
    if (!scope.active || current.current !== scope) return;
    scope.request?.controller.abort(); scope.request = null;
    publish(idle());
  }, [scope, publish]);
  return useMemo(() => ({ ...prepared, presentation: selectArtifactReadPresentation(prepared), select, retry, acceptSaved, clear }), [prepared, select, retry, acceptSaved, clear]);
};
